import { SwapOutlined } from "@ant-design/icons";
import {
  Collapse,
  Descriptions as AntdDescriptions,
  Empty,
  List,
  Space,
  Tag as AntdTag,
  Typography,
} from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ICustomerSatisfactionAudit } from "../../../models/customer-satisfaction/response";
import { getCustomerDissatisfactionOperationalSignalLabel } from "../../../models/customer-satisfaction/enum";

const { Panel } = Collapse;

interface IProps {
  audits: ICustomerSatisfactionAudit[];
  loading?: boolean;
  showOrder?: boolean;
}

const formatUserName = (user?: any) => {
  if (!user) return "-";
  return (
    user.fullName ||
    user.name ||
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
    `#${user.id}`
  );
};

const getSignalText = (value: any, t: (key: string) => string) => {
  if (typeof value === "string") {
    return t(getCustomerDissatisfactionOperationalSignalLabel(value));
  }

  const key =
    value?.key ??
    value?.operational_signal ??
    value?.operationalSignal ??
    value?.value;

  if (key) return t(getCustomerDissatisfactionOperationalSignalLabel(key));
  return undefined;
};

const getDisplayText = (value: any, t: (key: string) => string) => {
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) {
    const labels = value.map((item) => getSignalText(item, t)).filter(Boolean);
    return labels.length > 0 ? labels.join(", ") : "-";
  }
  if (typeof value === "object" && "reason" in value) {
    return value.reason ?? (value.id ? `#${value.id}` : "-");
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const AuditChangesList: React.FC<IProps> = ({ audits, loading, showOrder }) => {
  const { t } = useTranslation();

  const getFieldLabel = (field: string) => {
    const labels: Record<string, string> = {
      status: t("customer_satisfaction_field_status"),
      note: t("customer_satisfaction_field_note"),
      reason: t("customer_satisfaction_field_reason"),
      reasonId: t(
        "customer_satisfaction_field_customer_dissatisfaction_reason_id",
      ),
      customer_dissatisfaction_reason: t(
        "customer_satisfaction_field_customer_dissatisfaction_reason",
      ),
      customer_dissatisfaction_reason_id: t(
        "customer_satisfaction_field_customer_dissatisfaction_reason_id",
      ),
      operationalSignals: t("operational_signals"),
      operational_signals: t("operational_signals"),
    };

    return labels[field] ?? field;
  };

  const getReasonText = (
    reason?: {
      id: number;
      reason?: string | null;
      operationalSignals?: string[];
    } | null,
  ) => {
    if (!reason) return undefined;

    const signalText =
      reason.operationalSignals && reason.operationalSignals.length > 0
        ? ` (${getDisplayText(reason.operationalSignals, t)})`
        : "";

    return `${reason.reason ?? (reason.id ? `#${reason.id}` : "-")}${signalText}`;
  };

  const getChangeDisplayText = (
    displayValue: any,
    value: any,
    reason?: {
      id: number;
      reason?: string | null;
      operationalSignals?: string[];
    } | null,
  ) => getDisplayText(displayValue ?? getReasonText(reason) ?? value, t);

  const renderTechnicalValues = (values?: Record<string, any> | null) => {
    if (!values || Object.keys(values).length === 0) return "-";

    return (
      <Space direction="vertical" size={4}>
        {Object.entries(values).map(([key, value]) => (
          <span key={key}>
            <Typography.Text type="secondary">
              {getFieldLabel(key)}:{" "}
            </Typography.Text>
            {getDisplayText(value, t)}
          </span>
        ))}
      </Space>
    );
  };

  return (
    <List<ICustomerSatisfactionAudit>
      dataSource={audits}
      loading={loading}
      locale={{ emptyText: <Empty description={t("no_data")} /> }}
      renderItem={(audit) => (
        <List.Item>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Space size="small" wrap>
              <AntdTag color="blue">
                {t(`audit_action_${audit.action}`)}
              </AntdTag>
              {showOrder && audit.order?.id && (
                <Link to={`/orders/details/${audit.order.id}`}>
                  <AntdTag color="geekblue">
                    {t("order_number")}: {audit.order.number ?? audit.order.id}
                  </AntdTag>
                </Link>
              )}
              <Typography.Text strong>
                {formatUserName(audit.changedBy)}
              </Typography.Text>
              <Typography.Text type="secondary">
                {audit.createdAt
                  ? moment(audit.createdAt).format("YYYY-MM-DD HH:mm:ss")
                  : "-"}
              </Typography.Text>
            </Space>

            {audit.changes && audit.changes.length > 0 ? (
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                {audit.changes.map((change, index) => (
                  <Space key={`${change.field}-${index}`} size="small" wrap>
                    <AntdTag>{getFieldLabel(change.field)}</AntdTag>
                    <Typography.Text type="secondary">
                      {getChangeDisplayText(
                        change.oldDisplayValue,
                        change.oldValue,
                        change.oldReason,
                      )}
                    </Typography.Text>
                    <SwapOutlined />
                    <Typography.Text strong>
                      {getChangeDisplayText(
                        change.newDisplayValue,
                        change.newValue,
                        change.newReason,
                      )}
                    </Typography.Text>
                  </Space>
                ))}
              </Space>
            ) : (
              <Typography.Text type="secondary">
                {t("no_audit_changes")}
              </Typography.Text>
            )}

            {(audit.oldValues || audit.newValues) && (
              <Collapse ghost>
                <Panel header={t("technical_details")} key="technical">
                  <AntdDescriptions size="small" column={1} bordered>
                    <AntdDescriptions.Item label={t("old_values")}>
                      {renderTechnicalValues(audit.oldValues)}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("new_values")}>
                      {renderTechnicalValues(audit.newValues)}
                    </AntdDescriptions.Item>
                  </AntdDescriptions>
                </Panel>
              </Collapse>
            )}
          </Space>
        </List.Item>
      )}
    />
  );
};

export default AuditChangesList;
